import {
  DashboardCardContent,
  DashboardCardHeader,
  DashboardCard,
} from '@pages/dashboard/components';
import { CurrencyFormatted, Typography, Button } from '@ui/components';
import { CheckImpersonation, HasPermission } from '@shared/components';
import { useGetDashboard } from '@pages/dashboard/hooks';
import { NavWallet } from '@ui/components/icons';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import React, { useState } from 'react';
import { cn } from '@ui/utils';

import { DashboardWalletProgressBar } from './dashboard-wallet-progress-bar';
import { DashboardWalletAccounts } from './dashboard-wallet-accounts';
import { DashboardWalletDrawer } from './dashboard-wallet-drawer';
import { DashboardWalletFooter } from './dashboard-wallet-footer';

interface DashboardWalletProps extends React.ComponentPropsWithoutRef<'div'> {
  onTopUpClick: () => void;
}

export const DashboardWallet = (props: DashboardWalletProps) => {
  const { className, onTopUpClick, ...rest } = props;
  const { currency } = useCurrentUser();
  const { t } = useTranslation();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { data, isLoading } = useGetDashboard();

  const accountsCount = data?.accounts?.length ?? 0;
  const totalBalance = data?.accounts?.reduce((acc, account) => acc + account.balance, 0) ?? 0;

  return (
    <DashboardCard className={cn(className)} {...rest}>
      <DashboardCardHeader
        action={
          <HasPermission
            permissionFeature="DASHBOARD_ACCOUNT_SEE_ACCOUNT_DETAILS"
            permissionType="READ"
          >
            <Button onClick={() => setIsDrawerOpen(true)} variant="link">
              {t('DASHBOARD.WALLET.SEE_DETAILS')}
            </Button>
          </HasPermission>
        }
        title={t('DASHBOARD.WALLET.TITLE')}
        icon={<NavWallet />}
        className="mb-8"
      />
      <DashboardCardContent className="overflow-visible" isLoading={isLoading}>
        <Typography variant="labelMediumMedium" className="text-weak">
          {accountsCount <= 1
            ? t('DASHBOARD.WALLET.SINGLE_ACCOUNT_BALANCE')
            : t('DASHBOARD.WALLET.MULTIPLE_ACCOUNT_BALANCE', { count: accountsCount })}
        </Typography>
        <Typography
          className={cn('mb-8', {
            'text-critical': totalBalance <= 0,
          })}
          variant="h600"
          asChild
        >
          <CurrencyFormatted value={totalBalance} currency={currency} />
        </Typography>

        {accountsCount === 1 && data?.accounts?.[0]?.daysLeft && data.accounts[0].daysLeft >= 0 && (
          <DashboardWalletProgressBar account={data.accounts[0]!} />
        )}

        {accountsCount > 1 && (
          <HasPermission
            permissionFeature="DASHBOARD_ACCOUNT_SEE_ACCOUNT_DETAILS"
            permissionType="READ"
          >
            <DashboardWalletAccounts
              openAccountsDetailsDialog={onTopUpClick}
              accounts={data?.accounts ?? []}
            />
          </HasPermission>
        )}

        <CheckImpersonation>
          <HasPermission permissionFeature="DASHBOARD_ACCOUNT_TOP_UP" permissionType="WRITE">
            <DashboardWalletFooter onCtaClick={onTopUpClick} />
          </HasPermission>
        </CheckImpersonation>

        <HasPermission
          permissionFeature="DASHBOARD_ACCOUNT_SEE_ACCOUNT_DETAILS"
          permissionType="READ"
        >
          <DashboardWalletDrawer
            onClose={() => setIsDrawerOpen(false)}
            accounts={data?.accounts ?? []}
            topUps={data?.topUps ?? []}
            onCtaClick={onTopUpClick}
            isOpen={isDrawerOpen}
          />
        </HasPermission>
      </DashboardCardContent>
    </DashboardCard>
  );
};
